// Scroll-into-view for TTSView's ParamPanel after a user-click activation.
// Extracted from TTSView unchanged — pure DOM geometry, no React state.

/** Walks up the DOM tree and returns the nearest ancestor that is an
 *  actual vertical scroll container. Used by scroll-into-view logic
 *  after the app-shell rework — the window is no longer scrollable, the
 *  content pane is. */
function findScrollContainer(el: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = el.parentElement;
  while (node) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

// After a user-click selects a new block, scroll the CONTENT PANE so the
// entire ParamPanel sits inside the visible area between the sticky
// timeline and the sticky bottom bar. The panel's margin-top is set
// synchronously by ParamPanel's layout effect, but the visual transition
// runs over 250ms — so the panel's *current* rect mid-transition lies
// about its final position. We project the panel's FINAL rect from
// (col wrapper top) + (inner panel's already-set style.marginTop) +
// (panel height, which doesn't transition). When clipped below, scroll
// down so the bottom edge sits inside; when clipped above, scroll up so
// the top edge sits inside. If panel is taller than the visible band,
// top-align it (we can't satisfy both).
export function scrollPanelIntoView(
  blockEl: HTMLElement | null | undefined,
  colEl: HTMLElement | null,
  stickyEl: HTMLElement | null,
): void {
  if (!blockEl) return;
  const scroller = findScrollContainer(blockEl);
  if (!scroller) return;
  const paneRect = scroller.getBoundingClientRect();
  const stickyBottom = stickyEl
    ? stickyEl.getBoundingClientRect().bottom
    : paneRect.top;
  // BottomBar lives inside the scroller — find it for the lower bound.
  const bottomBarEl = scroller.querySelector<HTMLElement>(
    '[data-sticky-bottom-bar="1"]',
  );
  const paneBottom = bottomBarEl
    ? bottomBarEl.getBoundingClientRect().top
    : paneRect.bottom;
  const margin = 8;
  const desiredTop = stickyBottom + margin;
  const desiredBottom = paneBottom - margin;

  // Project the panel's FINAL position so we don't read a mid-transition
  // rect. The .col wrapper doesn't transition (it stays at layout origin);
  // the inner .panel's marginTop is already set by ParamPanel's layout
  // effect (only its visual position is animating). Panel height is
  // stable (model swap is the only height changer, and that uses bumpSkip
  // to skip the transition).
  const innerPanel = colEl?.querySelector<HTMLElement>(
    '[data-param-panel="inner"]',
  );
  let projectedTop: number;
  let projectedHeight: number;
  if (colEl && innerPanel) {
    const colRect = colEl.getBoundingClientRect();
    const panelMarginTop = parseFloat(innerPanel.style.marginTop) || 0;
    // .panel uses `transform: translateY(-2px)` — bake it into the
    // projected top so our math matches the rendered geometry.
    const PANEL_TRANSLATE_Y = -2;
    projectedTop = colRect.top + panelMarginTop + PANEL_TRANSLATE_Y;
    projectedHeight = innerPanel.offsetHeight;
  } else {
    // Fallback: no panel mounted yet — use the block rect.
    const r = blockEl.getBoundingClientRect();
    projectedTop = r.top;
    projectedHeight = r.height;
  }
  const projectedBottom = projectedTop + projectedHeight;

  const clipAbove = Math.max(0, desiredTop - projectedTop);
  const clipBelow = Math.max(0, projectedBottom - desiredBottom);
  if (clipAbove === 0 && clipBelow === 0) return;

  // If the panel is taller than the visible window between sticky bars,
  // top-align it (we can't satisfy both).
  const visibleH = desiredBottom - desiredTop;
  if (projectedHeight > visibleH) {
    scroller.scrollBy({
      top: projectedTop - desiredTop,
      behavior: 'smooth',
    });
    return;
  }
  // Pick the shorter scroll: if clipped above, scroll up to reveal top.
  // If clipped below, scroll down so the BOTTOM edge sits inside the
  // visible band — this is the case the user reported broken when the
  // panel moves DOWN to a lower block.
  if (clipAbove > 0 && clipBelow > 0) {
    if (clipAbove <= clipBelow) {
      scroller.scrollBy({ top: -clipAbove, behavior: 'smooth' });
    } else {
      scroller.scrollBy({ top: clipBelow, behavior: 'smooth' });
    }
    return;
  }
  if (clipAbove > 0) {
    scroller.scrollBy({ top: -clipAbove, behavior: 'smooth' });
  } else {
    scroller.scrollBy({ top: clipBelow, behavior: 'smooth' });
  }
}
